import { useState, useEffect } from "react";
import AdminHeader from "../../component/AdminHeader";
import { AnimatePresence, motion } from "framer-motion";
import { Edit, ImagePlus, Layers, Trash2, X } from "lucide-react";
import { FormInput, FormSelect, FormTextArea } from "../../helpers/AdminHelper";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import StatCard from "../../component/StatCard";
import ActionButtons from "../../component/AdminActionButtons";

function CreateSubCategory({
  handleSubmit,
  closeModal,
  subCategoryDetails,
  setSubCategoryDetails,
  categories,
  preview,
  setPreview,
}) {
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-4 py-3 rounded-xl mt-2 outline-none focus:border-blue-500 transition";

  const handleChange = (key, value) => {
    setSubCategoryDetails((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    handleChange("subCategoryImage", file);
  };

  const removeLogo = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    handleChange("subCategoryImage", null);
  };

  return (
    <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50">
      <motion.div
        className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[95%]
        h-[90vh]
        bg-[#0a0f1a]
        border border-white/10
        rounded-2xl
        shadow-2xl
        overflow-hidden
        flex flex-col 
        "
      >
        <div
          className="
          flex items-center justify-between
          px-8 py-5
          border-b border-white/10
          bg-white/3
          "
        >
          <div>
            <h3 className="text-xl font-black text-white uppercase">
              Create Sub Category{" "}
            </h3>
            <p>Add Sub Category details and logo </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="h-10 w-10 rounded-xl bg-white/5 
          hover:bg-red-500 text-slate-300 flex items-center justify-center 
          "
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-5 overflow-y-auto flex-1"
        >
          <div>
            <label className="text-sm text-slate-300">Sub Category Logo</label>

            {preview ? (
              <div
                className="
        mt-2 relative h-36 rounded-xl
        border border-white/10
        bg-white/5
        flex items-center justify-center
        overflow-hidden
      "
              >
                <img
                  src={preview}
                  alt="Sub Category preview"
                  className="h-full w-full object-contain"
                />

                <button
                  type="button"
                  onClick={removeLogo}
                  className="
          absolute top-2 right-2
          h-8 w-8 rounded-full
          bg-red-500 text-white
          flex items-center justify-center"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label
                className="
        mt-2 h-36 rounded-xl
        border border-dashed border-white/20
        bg-white/5
        flex flex-col items-center
        justify-center cursor-pointer
      "
              >
                <ImagePlus size={30} />

                <span className="text-sm mt-2">Upload Logo</span>

                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </div>

          <FormInput
            label="Sub Category Name"
            className={inputClass}
            placeholder="Auto Dialers"
            value={subCategoryDetails.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />

          <FormSelect
            label="Category"
            value={subCategoryDetails.category}
            onChange={(e) => handleChange("category", e.target.value)}
            placeholder="Select Category"
            options={categories.map((category) => ({
              value: category._id,
              label: category.name,
            }))}
          />

          <FormInput
            label="Slug"
            className={inputClass}
            placeholder="cctv-cameras"
            value={subCategoryDetails.slug}
            onChange={(e) => handleChange("slug", e.target.value)}
          />

          <FormTextArea
            label="Description"
            rows={4}
            className={inputClass}
            placeholder="Write Sub Category description"
            value={subCategoryDetails.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />

          <FormInput
            label="Meta Title"
            className={inputClass}
            placeholder="Best CCTV Camera"
            value={subCategoryDetails.metaTitle}
            onChange={(e) => handleChange("metaTitle", e.target.value)}
          />

          <FormTextArea
            label="Meta Description"
            rows={3}
            className={inputClass}
            placeholder="SEO description..."
            value={subCategoryDetails.metaDescription}
            onChange={(e) => handleChange("metaDescription", e.target.value)}
          />

          <FormInput
            label="Meta Tags"
            className={inputClass}
            placeholder="camera,cctv,security"
            value={subCategoryDetails.metaTags}
            onChange={(e) => handleChange("metaTags", e.target.value)}
          />
          <FormTextArea
            label="Schema Script (JSON-LD)"
            rows={8}
            placeholder={`{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "CCTV Camera"
}`}
            value={subCategoryDetails.schemaScript || ""}
            onChange={(e) => handleChange("schemaScript", e.target.value)}
          />

          <p className="text-xs text-slate-400 mt-2">
            Add valid JSON-LD schema only
          </p>

          <div
            className="
    flex justify-end gap-3
    pt-5
    border-t border-white/10
  "
          >
            <button
              type="button"
              onClick={closeModal}
              className="
      px-6 py-3
      rounded-xl
      bg-white/5
      hover:bg-white/10
      "
            >
              Cancel
            </button>

            <button
              type="submit"
              className="
      px-6 py-3
      rounded-xl
      bg-blue-600
      hover:bg-blue-700
      text-white
      "
            >
              Create Sub Category
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const AdminSubCategory = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [totalSubCategoryCount, setTotalSubCategoryCount] = useState(null);
  const [totalCategoryCount, setTotalCategoryCount] = useState(null);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [subCategoryDetails, setSubCategoryDetails] = useState({
    name: "",
    category: "",
    slug: "",
    description: "",
    subCategoryImage: null,
    metaTitle: "",
    metaDescription: "",
    metaTags: "",
    schemaScript: "",
  });
  const [preview, setPreview] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 10,
  });

  const resetForm = () => {
    setSubCategoryDetails({
      name: "",
      category: "",
      slug: "",
      description: "",
      subCategoryImage: null,
      metaTitle: "",
      metaDescription: "",
      metaTags: "",
      schemaScript: "",
    });
    setPreview(null);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const isValidSlug = (slug) => {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId && !subCategoryDetails.subCategoryImage) {
      alert("Image required");
      return;
    }

    if (!subCategoryDetails.name?.trim()) {
      alert("Sub Category name is required");
      return;
    }

    if (!subCategoryDetails.slug?.trim()) {
      alert("Slug is required");
      return;
    }

    if (!isValidSlug(subCategoryDetails.slug)) {
      alert("Invalid slug format");
      return;
    }

    if (!subCategoryDetails?.description) {
      alert("Desciption is required");
      return;
    }

    if (!subCategoryDetails.metaTitle?.trim()) {
      alert("Meta Title is required");
      return;
    }

    if (!subCategoryDetails.metaDescription?.trim()) {
      alert("Meta Description is required");
      return;
    }

    if (!subCategoryDetails.metaTags?.trim()) {
      alert("Meta Tags is required");
      return;
    }

    if (!subCategoryDetails?.category?.trim()) {
      alert("Sub Category, Category is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", subCategoryDetails?.name);
    formData.append("category", subCategoryDetails?.category);
    formData.append("slug", subCategoryDetails?.slug);
    formData.append("description", subCategoryDetails?.description);

    if (subCategoryDetails?.subCategoryImage) {
      formData.append("subCategoryImage", subCategoryDetails?.subCategoryImage);
    }
    formData.append("metaTitle", subCategoryDetails?.metaTitle);
    formData.append("metaDescription", subCategoryDetails?.metaDescription);

    if (subCategoryDetails?.metaTags) {
      const keywordArray = subCategoryDetails.metaTags
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
      formData.append("metaTags", JSON.stringify(keywordArray));
    }
    let schemaData = "";

    if (subCategoryDetails.schemaScript?.trim()) {
      try {
        JSON.parse(subCategoryDetails.schemaScript);
        schemaData = subCategoryDetails.schemaScript;
      } catch {
        alert("Invalid Schema JSON");
        return;
      }
    }

    formData.append("schemaScript", schemaData);

    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }
    try {
      if (editId) {
        await http.put(`/subCategory/update/${editId}`, formData);
      } else {
        await http.post("/subCategory", formData);
      }

      await fetchSubCategories();

      setEditId(null);
      closeModal();
    } catch (error) {
      console.log(error.response?.data || error.message);
    }
  };

  const openModal = () => {
    setSubCategoryDetails((prev) => ({
      ...prev,
      category: "",
    }));

    setEditId(null);
    resetForm();

    setIsModalOpen(true);
  };

  const fetchAllSubCategories = async () => {
    try {
      const res = await http.get("/subCategory", {
        params: {
          page: 1,
          limit: 1000,
        },
      });

      setAllSubCategories(res.data.subCategories || []);
    } catch (error) {
      console.error(
        "ALL SUBCATEGORY ERROR:",
        error.response?.data || error.message,
      );
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await http.get("/category", {
        params: {
          limit: 100,
          page: 1,
        },
      });

      setCategories(res.data.category);
      setTotalCategoryCount(res.data.total);
    } catch (error) {
      console.log(error.response?.data || error.message);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const res = await http.get("/subCategory", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
        },
      });

      setSubCategories(res.data.subCategories);
      setTotalSubCategoryCount(res.data.total);

      setPagination((prev) => ({
        ...prev,
        totalPages: res.data.totalPages,
      }));
    } catch (error) {
      console.log(error.response?.data || error.message);
    }
  };

  const editSubCategory = async (item) => {
    setSubCategoryDetails({
      name: item.name,
      category: item.category?._id || item.category,
      slug: item.slug,
      description: item.description,
      subCategoryImage: null,
      metaTitle: item.metaTitle,
      metaDescription: item.metaDescription,
      metaTags: Array.isArray(item.metaTags)
        ? item.metaTags.join(",")
        : item.metaTags || "",
      schemaScript: item.schemaScript || "",
    });

    setPreview(
      item.subCategoryImage ? GetImageUrl(item.subCategoryImage) : null,
    );

    setEditId(item._id);
    setIsModalOpen(true);
  };

  const handleMenuOrderChange = async (
  subCategoryId,
  menuOrder
) => {
  try {
    await http.patch(
      "/subCategory/menu-order",
      {
        subCategoryId,
        menuOrder: Number(menuOrder),
      }
    );

    await Promise.all([
      fetchSubCategories(),
      fetchAllSubCategories(),
    ]);
  } catch (error) {
    console.error(
      "SUBCATEGORY POSITION ERROR:",
      error.response?.data ||
        error.message
    );
  }
};

  /* const handleMenuOrderChange = async (subCategoryId, menuOrder) => {
    try {
      // console.log("SUBCATEGORY POSITION:", {
      //   subCategoryId,
      //   menuOrder: Number(menuOrder),
      // });

      await http.patch("/subCategory/menu-order", {
        subCategoryId,
        menuOrder: Number(menuOrder),
      });

      await fetchSubCategories();
    } catch (error) {
      console.error(
        "SUBCATEGORY POSITION ERROR:",
        error.response?.data || error.message,
      );
    }
  }; */

  /* useEffect(() => {
    fetchSubCategories();
  }, [pagination.page, pagination.limit]);
  useEffect(() => {
    fetchCategories();
  }, []); */

  useEffect(() => {
    fetchSubCategories();
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchAllSubCategories();
    fetchCategories();
  }, []);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200 ">
      <AdminHeader
        title="Product Sub Category"
        buttonText="Add Sub Category"
        onClick={() => openModal(true)}
      />

      <div className=" flex w-full mb-8  gap-4">
        <StatCard
          title="Total Categories"
          count={totalCategoryCount}
          icon={Layers}
        />

        <StatCard
          title="Total Sub Categories"
          count={totalSubCategoryCount}
          icon={Layers}
        />
      </div>
      {/** All SubCategories */}
      {subCategories.length === 0 ? (
        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center text-slate-400">
          No categories found
        </div>
      ) : (
        <div className="space-y-4">
          {subCategories.map((item) => (
            <div
              key={item._id}
              className="
      flex flex-col
      sm:flex-row
      sm:items-center
      justify-between
      gap-4
      p-2
      rounded
        bg-slate-900/40
        border border-slate-800
        hover:border-slate-700
        hover:bg-slate-900/60
      transition
    "
            >
              {/* Image + Details */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                  className="
          h-14 w-14
          sm:h-16 sm:w-16
          shrink-0
          rounded
          bg-white
          p-2
          flex items-center justify-center
        "
                >
                  <img
                    src={GetImageUrl(item.subCategoryImage)}
                    className="
            max-h-full
            max-w-full
            object-contain
          "
                  />
                </div>

                <div className="min-w-0">
                  <h3
                    className="
            text-white
            font-bold
          text-sm lg:text-base
            truncate
          "
                  >
                    {item.name}
                  </h3>

                  <div className="mt-1 space-y-1">
                    <p className="text-slate-400 text-[11px]  truncate">
                      Category: {item.category?.name}
                    </p>
                  </div>
                </div>
              </div>

              {/* <select
                value={item.menuOrder || ""}
                onChange={(e) =>
                  handleMenuOrderChange(item._id, e.target.value)
                }
                className="
    bg-slate-800
    border border-slate-700
    text-white
    text-sm
    rounded-lg
    px-3 py-2
    outline-none
  "
              >
                <option value="">Position</option>

                {Array.from(
                  {
                    length: subCategories.filter(
                      (sub) =>
                        (sub.category?._id || sub.category) ===
                        (item.category?._id || item.category),
                    ).length,
                  },
                  (_, index) => (
                    <option key={index + 1} value={index + 1}>
                      Position {index + 1}
                    </option>
                  ),
                )}
              </select> */}

              <select
                value={item.menuOrder || ""}
                onChange={(e) =>
                  handleMenuOrderChange(item._id, e.target.value)
                }
                className="
    bg-slate-800
    border border-slate-700
    text-white
    text-sm
    rounded-lg
    px-3 py-2
    outline-none
  "
              >
                <option value="">Position</option>

                {Array.from(
                  {
                    length: allSubCategories.filter(
                      (sub) =>
                        String(sub.category?._id || sub.category) ===
                        String(item.category?._id || item.category),
                    ).length,
                  },
                  (_, index) => (
                    <option key={index + 1} value={index + 1}>
                      Position {index + 1}
                    </option>
                  ),
                )}
              </select>

              {/* Action Buttons */}
              <ActionButtons
                onEdit={() => editSubCategory(item)}
                onDelete={async () => {
                  if (!confirm("Delete this sub category?")) return;

                  await http.delete(`/subCategory/delete/${item._id}`);
                  fetchSubCategories();
                }}
              />
            </div>
          ))}
        </div>
      )}
      {/** Pagination Controlls */}
      {subCategories.length > 0 && (
        <div
          className="
    flex 
    flex-col sm:flex-row
    justify-center 
    items-center 
    gap-3 sm:gap-4
    mt-8
    px-2
  "
        >
          <button
            disabled={pagination.page === 1}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: p.page - 1,
              }))
            }
            className="
        w-full sm:w-auto
        px-4 sm:px-5
        py-2
        rounded-xl
        bg-white/10
        hover:bg-white/20
        disabled:opacity-40
        transition
        text-sm sm:text-base
      "
          >
            Previous
          </button>

          <span
            className="
      text-slate-300
      text-sm sm:text-base
      whitespace-nowrap
    "
          >
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            disabled={
              !pagination.totalPages ||
              pagination.page === pagination.totalPages
            }
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: p.page + 1,
              }))
            }
            className="
        w-full sm:w-auto
        px-4 sm:px-5
        py-2
        rounded-xl
        bg-blue-600
        hover:bg-blue-700
        text-white
        disabled:opacity-40
        transition
        text-sm sm:text-base
      "
          >
            Next
          </button>
        </div>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <CreateSubCategory
            handleSubmit={handleSubmit}
            closeModal={closeModal}
            subCategoryDetails={subCategoryDetails}
            setSubCategoryDetails={setSubCategoryDetails}
            preview={preview}
            setPreview={setPreview}
            categories={categories}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminSubCategory;
